/**
 * utils/incrementType.ts
 * Pure utility functions for IncrementType domain
 *
 * Parametric by design: all functions accept data maps as parameters rather than
 * importing from `constants/incrementType`. This maintains utils purity per CODING_STANDARDS
 * and ensures functions remain testable in isolation.
 *
 * These are format/check helpers for IncrementType that maintain purity and avoid
 * hidden module dependencies.
 *
 * USAGE:
 * - Import meta map from `constants/incrementType` at call sites
 * - Pass maps as function arguments
 * - Functions are pure: same inputs always produce same outputs
 *
 * @example
 * ```ts
 * // Import meta map from constants (at call site, not in this file)
 * import { INCREMENT_TYPE_META } from '@/constants/incrementType';
 * import { formatIncrementTypeDisplay, shouldShowRawValue } from '@/utils/incrementType';
 *
 * // Pass map as argument (parametric pattern)
 * const display = formatIncrementTypeDisplay('tick', INCREMENT_TYPE_META);
 * // => 'Tick'
 *
 * const showRaw = shouldShowRawValue('cent', INCREMENT_TYPE_META);
 * // => true
 * ```
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero imports from `constants/` or other domain modules
 * - Only `import type` from `types/` (no runtime dependencies)
 * - No side effects (no logs, no mutations, no I/O, no caches)
 * - All functions are deterministic and testable
 * - **This file must not import constants** (parametric design requirement)
 */

// Internal absolute imports (type-only)
import type {
  IncrementType,
  IncrementTypeMeta
} from '@/types/shared/IncrementType';

/**
 * Format increment type for display
 *
 * Parametric formatter: accepts meta map as argument to maintain purity.
 * Falls back to raw value if key missing in map (allows UI to handle unknown values).
 *
 * @param incrementType - IncrementType value to format
 * @param metaMap - Record mapping IncrementType to IncrementTypeMeta objects
 * @returns Formatted display string (or raw value if not in map)
 *
 * @example
 * ```ts
 * import { INCREMENT_TYPE_META } from '@/constants/incrementType';
 *
 * const display = formatIncrementTypeDisplay('tick', INCREMENT_TYPE_META);
 * // => 'Tick'
 * ```
 */
export function formatIncrementTypeDisplay(
  incrementType: IncrementType,
  metaMap: Readonly<Record<IncrementType, IncrementTypeMeta>>
): string {
  return metaMap[incrementType]?.display ?? incrementType;
}

/**
 * Check if increment type should display raw currency values
 *
 * Parametric checker: accepts meta map as argument to maintain purity.
 * Falls back to false (prefer unit labels) if key missing in map (safe default for unknown increment types).
 *
 * Returns true for increment types that should display as raw currency values
 * (e.g., "$0.01" for cent) rather than unit labels (e.g., "1 tick = $0.25").
 *
 * @param incrementType - IncrementType value to check
 * @param metaMap - Record mapping IncrementType to IncrementTypeMeta objects
 * @returns true if should show raw values (e.g., "$0.01" for cent), false for unit labels (e.g., "1 tick = $0.25")
 *
 * @example
 * ```ts
 * import { INCREMENT_TYPE_META } from '@/constants/incrementType';
 *
 * // Cent shows raw values
 * shouldShowRawValue('cent', INCREMENT_TYPE_META);
 * // => true
 *
 * // Tick shows unit labels
 * shouldShowRawValue('tick', INCREMENT_TYPE_META);
 * // => false
 * ```
 */
export function shouldShowRawValue(
  incrementType: IncrementType,
  metaMap: Readonly<Record<IncrementType, IncrementTypeMeta>>
): boolean {
  return metaMap[incrementType]?.showRawValue ?? false;
}
